import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  noindex?: boolean;
  schema?: object;
}

/**
 * Hook para atualizar meta tags dinamicamente em cada página
 * Melhora o SEO permitindo título e descrição únicos por rota
 */
export const useSEO = ({ title, description, canonical, ogImage, noindex, schema }: SEOProps) => {
  useEffect(() => {
    // Update page title
    const fullTitle = title === 'Home'
      ? 'Clinafy | Sistema para Profissionais de Saúde Mental | Prontuário, Agenda e Teleconsulta'
      : `${title} | Clinafy - Sistema para Saúde Mental`;
    document.title = fullTitle;

    // Handle noindex for protected pages
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (noindex) {
      if (!robotsMeta) {
        robotsMeta = document.createElement('meta');
        robotsMeta.setAttribute('name', 'robots');
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.setAttribute('content', 'noindex, nofollow');
    } else if (robotsMeta) {
      robotsMeta.setAttribute('content', 'index, follow');
    }

    // Update meta description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', description);
    }

    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', title === 'Home' ? 'Clinafy - Sistema para Profissionais de Saúde Mental' : title);
    }

    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) {
      ogDesc.setAttribute('content', description);
    }

    if (ogImage) {
      const ogImg = document.querySelector('meta[property="og:image"]');
      if (ogImg) {
        ogImg.setAttribute('content', ogImage);
      }
    }

    // Update canonical URL if provided
    if (canonical) {
      const canonicalLink = document.querySelector('link[rel="canonical"]');
      if (canonicalLink) {
        canonicalLink.setAttribute('href', canonical);
      }

      const ogUrl = document.querySelector('meta[property="og:url"]');
      if (ogUrl) {
        ogUrl.setAttribute('content', canonical);
      }
    }

    // Update Twitter Card tags
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', title === 'Home' ? 'Clinafy - Sistema para Profissionais de Saúde Mental' : title);
    }

    const twitterDesc = document.querySelector('meta[name="twitter:description"]');
    if (twitterDesc) {
      twitterDesc.setAttribute('content', description);
    }

    // Inject JSON-LD schema markup
    let schemaScript = document.getElementById('dynamic-schema-ld');
    if (schema) {
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.id = 'dynamic-schema-ld';
        schemaScript.setAttribute('type', 'application/ld+json');
        document.head.appendChild(schemaScript);
      }
      schemaScript.textContent = JSON.stringify(schema);
    } else if (schemaScript) {
      schemaScript.remove();
    }

    // Cleanup: restore default values when component unmounts
    return () => {
      document.title = 'Clinafy | Sistema para Profissionais de Saúde Mental | Prontuário, Agenda e Teleconsulta';
      const dynamicSchema = document.getElementById('dynamic-schema-ld');
      if (dynamicSchema) dynamicSchema.remove();
      // Restore robots meta
      const robots = document.querySelector('meta[name="robots"]');
      if (robots) robots.setAttribute('content', 'index, follow');
    };
  }, [title, description, canonical, ogImage, noindex, schema]);
};

// SEO configurations for each public page
export const SEO_CONFIG = {
  home: {
    title: 'Home',
    description: 'Sistema completo para profissionais de saúde mental: prontuário eletrônico com IA, agenda inteligente, teleconsulta HD e gestão financeira. Teste grátis 30 dias. +500 profissionais confiam na Clinafy.',
    canonical: 'https://clinafy.com/'
  },
  funcionalidades: {
    title: 'Funcionalidades',
    description: 'Conheça todas as funcionalidades do Clinafy: prontuário eletrônico inteligente, agenda com lembretes automáticos, teleconsulta em HD, transcrição com IA e gestão financeira completa.',
    canonical: 'https://clinafy.com/funcionalidades'
  },
  planos: {
    title: 'Planos e Preços',
    description: 'Planos Clinafy a partir de R$19,90/mês. Escolha o plano ideal para seu consultório ou clínica. Trial grátis de 30 dias. Sem fidelidade, cancele quando quiser.',
    canonical: 'https://clinafy.com/planos'
  },
  sobre: {
    title: 'Sobre Nós',
    description: 'Conheça a Clinafy: nossa missão é simplificar a gestão de consultórios de saúde mental. +500 profissionais confiam em nós. Empresa brasileira com suporte humanizado.',
    canonical: 'https://clinafy.com/sobre'
  },
  contato: {
    title: 'Contato',
    description: 'Entre em contato com a Clinafy. Suporte via WhatsApp e email com resposta em até 2 horas. Tire suas dúvidas ou agende uma demonstração gratuita.',
    canonical: 'https://clinafy.com/contato'
  },
  login: {
    title: 'Login',
    description: 'Acesse sua conta Clinafy. Sistema seguro para profissionais de saúde mental. Prontuário eletrônico, agenda e teleconsulta em um só lugar.',
    canonical: 'https://clinafy.com/login'
  },
  signup: {
    title: 'Criar Conta - Teste Grátis',
    description: 'Crie sua conta Clinafy e comece seu teste grátis de 30 dias. Sem cartão de crédito. Acesso imediato a todas as funcionalidades.',
    canonical: 'https://clinafy.com/signup'
  },
  blog: {
    title: 'Blog',
    description: 'Dicas, guias e novidades para profissionais de saúde mental. Aprenda sobre prontuário eletrônico, gestão de consultório, teleconsulta, LGPD e muito mais.',
    canonical: 'https://clinafy.com/blog'
  }
};

export default useSEO;
